import type { CameraState } from '@h2hspark/ui/camera';
import type { QRCodeToStringOptions } from 'qrcode';

import type { ScanOutcome } from './types';

/**
 * The Connect area's copy, and every decision in it (`MEM-028`…`MEM-031`).
 *
 * **There is no DOM renderer in this repository and `apps/web`'s test script matches `.test.ts`
 * only**, so a rule that lives inside a component body is a rule with no test.
 * `event-tickets/constants.ts` states the convention; this file follows it, and the two things on
 * this screen that could be *wrong* — which code goes in the QR, and whether a failed scan can ever
 * look like a success — are functions over data here rather than event handlers in JSX.
 *
 * Nothing in this file imports the `@/` alias. `tsx --test` resolves relative specifiers and not
 * Vite's aliases, so a pure module that reached for `@/` would be a pure module the test runner
 * could not load.
 */

// ─── MEM-028 AC-3: the connect code ─────────────────────────────────────────────────────────────

/** The screen's title, and the prototype's own `Connect` heading. */
export const CONNECT_HEADING = 'Connect';

/**
 * `MEM-EVT-003`, verbatim: the card is *headed "Tap to connect"* with the supporting line
 * *"Have them scan this — one tap logs the meeting"*.
 *
 * Both strings are the requirement rather than a designer's placeholder, which is why they are
 * constants with a test rather than text in a component. The em dash is the prototype's.
 */
export const CONNECT_CODE_HEADING = 'Tap to connect';
export const CONNECT_CODE_SUPPORTING_LINE = 'Have them scan this — one tap logs the meeting';

/** `MEM-028` AC-5's second entry point — the prompt beneath the code, which opens the scanner. */
export const CONNECT_CODE_SCAN_PROMPT = 'Tap to log a new connection';

/** `MEM-028` AC-5's first entry point — the "Scan" control in the Connect header. */
export const SCAN_ACTION_LABEL = 'Scan';

/**
 * The `alt` on the connect QR.
 *
 * It describes the image's *purpose*, because its content — a 43-character token — is not
 * something a screen-reader user can act on, and reading it aloud in a room would hand the code to
 * everyone within earshot (NFR accessibility#wcag-aa). Same decision the ticket makes, for the
 * same reason.
 */
export const CONNECT_QR_ALTERNATIVE_TEXT = 'A QR code for another member to scan.';

/** The shape of a token the API mints: 43 base64url characters, which is 32 random bytes encoded. */
export const MEMBER_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * `MEM-028` AC-2 and AC-3: **the QR carries the connect token.**
 *
 * ## The signature is the enforcement
 *
 * This takes `{ connectToken }` and not the whole surface, so the ticket token is not in scope at
 * the one point in this screen that decides what gets encoded — the exact mirror of
 * `event-tickets/constants.ts`'s `qrPayload`, which takes `{ ticketToken }` and cannot see this
 * one.
 *
 * That symmetry *is* AC-2. "The member never has to know which is which" is honoured by each
 * surface being handed the one code it is for: the two are indistinguishable to look at, both 43
 * base64url characters, and a screen that quietly encoded the other would scan and then do the
 * wrong thing rather than fail. The type system is a cheaper guard against that than a code
 * review. (The API helps: the Connect payload does not carry the ticket token at all.)
 *
 * Returns `null` when the value is not token-shaped, so the screen can say the code is not ready
 * instead of drawing a square that scans to nonsense in a dark room.
 */
export function connectQrPayload(surface: { readonly connectToken: string }): string | null {
  return MEMBER_TOKEN_PATTERN.test(surface.connectToken) ? surface.connectToken : null;
}

/**
 * How the connect code is drawn.
 *
 * SVG rather than a raster, error correction `M`, two modules of quiet zone, and no colour set —
 * every one of those is `TICKET_QR_OPTIONS`' reasoning, unchanged, and it applies harder here: the
 * ticket is read under a door's lighting by a volunteer expecting it, and this is read across a
 * table by somebody who has never used the app.
 *
 * The prototype draws the card on `--purple-800` with the code on a white tile. That is the
 * arrangement to keep: a branded purple QR is the single most common way to make a code that
 * photographs beautifully and reads badly, so the *card* is dark and the *code* stays black on
 * white.
 */
export const CONNECT_QR_OPTIONS: QRCodeToStringOptions = {
  type: 'svg',
  errorCorrectionLevel: 'M',
  margin: 2,
};

/** An SVG the `qrcode` renderer produced, addressed as an image source. */
export function qrSvgDataUri(svgMarkup: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svgMarkup)}`;
}

/** Where the Connect area lives. One function, so every link to it agrees. */
export function connectPath(eventId: string): string {
  return `/app/events/${encodeURIComponent(eventId)}/connect`;
}

export const CONNECT_ACTION_LABEL = 'Connect';
export const BACK_TO_EVENT_LABEL = 'Back to event';

// ─── MEM-030: open to connect, the roster, people you met ───────────────────────────────────────

/** The prototype's own row: a title, and a line saying what the switch actually does. */
export const OPEN_TO_CONNECT_LABEL = 'Open to connect';
export const OPEN_TO_CONNECT_ON_DESCRIPTION = "Others can see you're available";
/**
 * What it says when it is off.
 *
 * **It must not read as a lock.** `MEM-030` AC-1 is emphatic that the status *"never enforces
 * anything"* — somebody with it off can still be scanned by the person they are talking to — so
 * the off state says the member has left a *list*, not that they have closed a door. "You're
 * hidden" or "Nobody can connect with you" would both be untrue, and the second would be a promise
 * the product does not keep.
 */
export const OPEN_TO_CONNECT_OFF_DESCRIPTION = "You're not in the room's list right now";

/**
 * **The in-event suggestion prompt's heading** (`MEM-030` AC-8, `MSG-029`).
 *
 * The *sentence* under it is served — `PROMPT_SENTENCES` on the API — because it differs by reason
 * and what a member reads has to be what the server actually decided. Only the heading is here, and
 * it is deliberately not a name: the name is in the sentence, once, so the screen has one place it
 * renders a person and cannot grow a second.
 */
export const SUGGESTION_HEADING = 'Someone worth saying hello to';

/** The prompt's control. It goes to the roster row it is about, which is on this same screen. */
export const SUGGESTION_ACTION_LABEL = 'Find them below';

/**
 * The roster section's DOM id, so the prompt's control can reach it.
 *
 * A fragment link rather than a `scrollIntoView` call: it works with the keyboard, it works before
 * hydration, and it is the one navigation on this screen that never leaves the page.
 */
export const ROSTER_REGION_ID = 'in-event-roster';

export const ROSTER_HEADING = 'Who else is here';
export const ROSTER_EMPTY_TITLE = 'Nobody else yet';
export const ROSTER_EMPTY_DESCRIPTION =
  'People appear here as the team checks them in, if they are open to being approached.';

export const PEOPLE_YOU_MET_HEADING = 'People you met';
/**
 * The empty state under "People you met", before the first scan of the event.
 *
 * Its neighbour "Who else is here" has had one since it was built, and a screen whose two lists
 * disagree about whether an empty list says anything reads as unfinished — the heading and a `0`
 * over blank space is the shape a member sees for most of an evening, because logging someone is
 * the last thing that happens in a conversation rather than the first.
 */
export const PEOPLE_YOU_MET_EMPTY_TITLE = 'Nobody logged yet';
export const PEOPLE_YOU_MET_EMPTY_DESCRIPTION =
  'Scan someone’s code, or have them scan yours, and they will appear here.';

/**
 * `MEM-030` AC-6's count badge, as a label rather than a bare number.
 *
 * The badge renders the digit; this is its accessible name, so a screen reader is told what the
 * number counts instead of reading "3" beside a heading. Same treatment `meetListCountLabel` gets.
 */
export function peopleYouMetCountLabel(count: number): string {
  return count === 1
    ? '1 person logged at this event'
    : `${String(count)} people logged at this event`;
}

/** The note hint on a met-card — the prototype's "Add a note" when there is none. */
export const ADD_A_NOTE_LABEL = 'Add a note';

// ─── the Connect page at desktop width (`Web App.dc.html` §2.6) ────────────────────────────────

/**
 * The page's heading and the sentence under it — §2.6, item 3, verbatim.
 *
 * The old screen headed itself *Connect*, which is the destination's name; the 10 September page
 * heads it *At the event*, because the header card above it already says which event this is and
 * the stepper already says *During · Connect*. The name of the place is not the heading of the page.
 */
export const AT_THE_EVENT_HEADING = 'At the event';
export const AT_THE_EVENT_SUBHEAD =
  "Log who you meet in one tap — it's what makes your follow-ups personal instead of generic.";

/** §2.6's outline-brand control at the right of the heading row, which opens the meet list. */
export const MEET_LIST_CONTROL_LABEL = 'People I want to meet';

/** §2.6's right column: *"People you met at this event"*, over the met-person cards. */
export const PEOPLE_YOU_MET_AT_THIS_EVENT_HEADING = 'People you met at this event';

/**
 * The status chip inside the purple panel, which §2.6 draws as a green dot and two words and which
 * *"is not a toggle"*.
 *
 * Here it is one. `MEM-030` AC-2 requires the status to be *"toggleable by the member at any time
 * while checked in"*, and the plan's own rule for the 10 September file is that **dead prototype
 * controls are wired to the real behaviour, never left dead**. So the chip keeps its place, its
 * dot and its words, and pressing it does what the switch on the old screen did.
 */
export const OPEN_TO_CONNECT_OFF_LABEL = 'Not open to connect';

// ─── the note composer (`Web App.dc.html` §2.16, `noteOpen`) ────────────────────────────────────

export function noteComposerTitle(firstName: string): string {
  return `Note about ${firstName}`;
}

export const NOTE_COMPOSER_FIELD_LABEL = 'What do you want to remember?';
export const NOTE_COMPOSER_PRIVACY_LINE =
  'Only you can see this. It is never shown to them, to the organizer, or to anybody else.';
export const NOTE_COMPOSER_SAVE_LABEL = 'Save note';
/** Emptying the field removes the note — never the connection (`MEM-031` AC-4). */
export const NOTE_COMPOSER_REMOVE_LABEL = 'Remove note';
export const NOTE_COMPOSER_CANCEL_LABEL = 'Cancel';
export const NOTE_COMPOSER_CLOSE_LABEL = 'Close the note';
export const NOTE_SAVE_FAILED = 'That note did not save. Try again.';

// ─── MEM-029: the scan, the capture sheet, the notice ───────────────────────────────────────────

export const SCANNER_HEADING = 'Scan to connect';
/** The prototype's line under the frame. */
export const SCANNER_PROMPT = "Point at another member's H2HSpark code to log the connection.";
export const SCANNER_CLOSE_LABEL = 'Close scanner';

// ─── the camera (`MEM-028` AC-5, `MEM-029` AC-1) ────────────────────────────────────────────────

/*
 * The camera's state, how a `getUserMedia` rejection is classified, the constraints and the frame
 * interval moved to `@h2hspark/ui/camera` when the check-in screen became the second scanner
 * (`EVT-042` AC-6). What a member is *told* about each state stays here, below: it is this screen's
 * copy, and it points at this screen's paste field.
 */

/**
 * What the member reads for each camera state.
 *
 * Every one of them ends at the same place — **the field below still works** — because that is the
 * true and useful thing: the token path is the same whether a lens or a keyboard fed it, so a
 * member whose camera is refused has not lost the feature, only the fast way into it.
 *
 * *"Nothing has been logged"* is on none of these, deliberately: nothing has been *attempted*. That
 * sentence belongs to `SCAN_UNREACHABLE_MESSAGE` and `SCAN_FAILED_MESSAGE`, where a tap did happen
 * and a member could reasonably believe it landed (`MEM-031` AC-3).
 */
export const CAMERA_STARTING_MESSAGE = 'Starting your camera…';
export const CAMERA_DENIED_MESSAGE =
  'We do not have permission to use your camera. Allow it in your browser to scan, or paste their code below.';
export const CAMERA_UNAVAILABLE_MESSAGE =
  'We could not find a camera on this device. You can still paste their code below.';
export const CAMERA_FAILED_MESSAGE =
  'The camera would not start. Try closing this and opening it again, or paste their code below.';

export function cameraMessage(state: CameraState): string | null {
  switch (state.kind) {
    case 'starting':
      return CAMERA_STARTING_MESSAGE;
    case 'live':
      return null;
    case 'denied':
      return CAMERA_DENIED_MESSAGE;
    case 'unavailable':
      return CAMERA_UNAVAILABLE_MESSAGE;
    case 'failed':
      return CAMERA_FAILED_MESSAGE;
  }
}

/**
 * **A code that is not one of ours.**
 *
 * A camera pointed at a room finds QR codes on table tents, menus and boarding passes.
 * `connectTokenFromScan` filters them out before anything is sent, so nothing is logged and nothing
 * is refused — but the member is holding the phone up at something and deserves to be told why it
 * is not working, rather than watching a viewfinder do nothing.
 */
export const NOT_A_CONNECT_CODE_MESSAGE =
  "That is a QR code, but not an H2HSpark connect code. Ask them to open Connect for this event and show you the code there.";

/**
 * **Is this string a connect code?** — the one filter between a camera frame and a scan request.
 *
 * A camera pointed at a room finds QR codes that are not ours: a wifi credential on a table tent, a
 * menu, somebody's boarding pass. Sending those to `POST /scan` would be a stream of refusals the
 * member cannot act on, and `MEM-031` AC-3's *fails visibly* would fire for something they never
 * aimed at. So a frame's payloads are filtered to the token shape first, and a frame with nothing
 * token-shaped in it is simply a frame with nothing in it — the scanner keeps looking.
 *
 * The shape is `MEMBER_TOKEN_PATTERN`'s: 43 base64url characters, which is 32 random bytes encoded.
 * A payload that is a **URL** ending in one is accepted too, because a code minted for a poster or
 * printed by another tool may well be a link — the token is taken from its last path segment, and
 * the pattern still has to hold.
 *
 * Here rather than beside the decoder since the decoder moved to `@h2hspark/ui/camera`
 * (`EVT-042` AC-6): the decoder reads codes, and what counts as a connect code is this app's rule.
 */
export function connectTokenFromScan(
  payload: string,
  pattern: RegExp,
): string | null {
  const trimmed = payload.trim();
  if (pattern.test(trimmed)) return trimmed;
  // The **path's** last segment: a query string and a fragment are dropped first, so a poster's
  // `?utm=` does not become the thing that is checked and then refused.
  const path = trimmed.split(/[?#]/u)[0] ?? '';
  const lastSegment = path
    .split('/')
    .filter((part) => part.length > 0)
    .pop();
  if (lastSegment !== undefined && pattern.test(lastSegment)) return lastSegment;
  return null;
}

/** The field under the viewfinder, which is the fallback and says so. */
export const SCANNER_PASTE_LABEL = 'Connect code';
export const SCANNER_PASTE_PLACEHOLDER = 'Or paste their connect code';
export const SCANNER_CAPTURE_LABEL = 'Capture code';

/**
 * The capture sheet's heading — `MEM-029` AC-3's *"immediately after logging"*.
 *
 * It is written in the **past tense on purpose**: by the time this renders the connection exists,
 * and a heading like "Log this connection?" would tell the member they still have something to
 * confirm. AC-1 forbids anything intervening, and copy is part of that — a prompt that reads like
 * a step is a step, whatever the code does.
 */
export function capturedHeading(firstName: string): string {
  return `Connected with ${firstName}`;
}

export const CAPTURED_SUBHEADING = 'Anything worth remembering while it is fresh?';
export const QUICK_NOTE_LABEL = 'Quick note';
export const QUICK_NOTE_PLACEHOLDER =
  'What you talked about, what to follow up on, anything you want to remember…';
export const QUICK_NOTE_PRIVACY_HINT = 'Private to you';
export const FOLLOW_UP_INTENT_LABEL = 'I want to follow up';
export const QUICK_NOTE_MAX_LENGTH = 500;

/**
 * `MEM-029` AC-4 — **both optional, and the skip control says so out loud.**
 *
 * "Skip" rather than "Cancel". Cancelling implies undoing the thing that just happened, and there
 * is nothing to undo: the connection is logged either way, and `MEM-031` AC-4 says it stays that
 * way. A member who taps this has not lost anything and should not think they might have.
 */
export const CAPTURE_SKIP_LABEL = 'Skip for now';
export const CAPTURE_SAVE_LABEL = 'Save';
/** Under the skip control, so the reassurance is where the hesitation is. */
export const CAPTURE_SKIP_HINT = 'Either way, they are already in your connections.';

export const NOTICE_OPEN_CONNECTION_LABEL = 'View connection';
export const NOTICE_DISMISS_LABEL = 'Dismiss';

// ─── MEM-029 AC-1: one tap, and nothing between the tap and the record ──────────────────────────

/**
 * What the Connect screen is doing, as one value.
 *
 * ## Why this is a state machine and not four `useState` booleans
 *
 * `MEM-029` AC-1 — *"one tap logs the connection … nothing is permitted to intervene"* — is a rule
 * about **what may exist between two moments**, and a rule about a sequence held in a component's
 * `useState` is a rule nobody can assert. `EVT-031` AC-5 got the same treatment on the organizer's
 * side (`check-in/constants.ts`'s `nextConnectionState`) for the same reason.
 *
 * The states are ordered, and the ordering is the requirement:
 *
 * ```
 *   idle → scanning → …one tap… → captured            (the connection exists, at this event)
 *                              ↘ connected-elsewhere  (it exists, and has no event — MEM-057)
 *                              ↘ refused              (nothing was written)
 *                              ↘ unreachable          (nothing was written)
 * ```
 *
 * Every one of those four is an **end state**. `connected-elsewhere` joined them on 2026-09-12 for
 * a profile connect code read by this event's scanner (`MEM-057` AC-7, ADR 053): the connection
 * exists but carries no event, so there is nothing for the capture sheet to write a note against
 * and the scanner closes with a sentence instead.
 *
 * **There is no state between `scanning` and `captured`.** No `confirming`, no `pending`, no
 * `awaiting-permission`. Camera permission belongs to the transition *into* `scanning` — the
 * scanner is opened deliberately, and asking there costs a member nothing; asking at the tap would
 * put a browser dialog between two people mid-conversation, which is exactly what the AC names.
 */
export type ScanStage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'scanning' }
  /** The connection exists. The capture sheet renders over this, and only over this. */
  | {
      readonly kind: 'captured';
      readonly attendeeRef: string;
      readonly firstName: string;
      readonly alreadyConnected: boolean;
    }
  | { readonly kind: 'refused'; readonly message: string }
  /**
   * A profile connect code was read here (`MEM-057` AC-7). The connection exists and has no
   * event, so there is nothing to capture against it — the scanner says so and closes.
   */
  | { readonly kind: 'connected-elsewhere'; readonly message: string }
  | { readonly kind: 'unreachable' };

export const INITIAL_SCAN_STAGE: ScanStage = { kind: 'idle' };

/**
 * What one scan attempt produced, as the screen sees it.
 *
 * `unreachable` is a request that never got an answer. It is deliberately a *different* variant
 * from a refusal, because they are different facts: a refusal is a conversation with a working
 * server, and an unreachable server is `MEM-031` AC-3's case, where nothing was written and the
 * member has to be told.
 */
export type ScanAttempt =
  | { readonly kind: 'answered'; readonly outcome: ScanOutcome }
  | { readonly kind: 'unreachable' };

/**
 * The stage one attempt moves the screen to.
 *
 * **A failure is never a success.** There is no branch here that turns an `unreachable` or a
 * `refused` into `captured` — `MEM-031` AC-3 says a scan that cannot reach the service *"fails
 * visibly and writes nothing"*, and the failure to avoid is an optimistic success state, where the
 * risk was accepted and the member was not told it had materialised. Two people would walk away
 * believing they had connected.
 *
 * `already-connected` **does** reach `captured`, and that is not a leak in the rule: the pair are
 * connected, which is what the member wanted to know. It is flagged so the sheet can say so rather
 * than pretending this tap did it.
 */
export function nextScanStage(attempt: ScanAttempt): ScanStage {
  if (attempt.kind === 'unreachable') return { kind: 'unreachable' };
  switch (attempt.outcome.result) {
    case 'logged':
      return {
        kind: 'captured',
        attendeeRef: attempt.outcome.connection.attendeeRef,
        firstName: attempt.outcome.connection.firstName,
        alreadyConnected: false,
      };
    case 'already-connected':
      return {
        kind: 'captured',
        attendeeRef: attempt.outcome.connection.attendeeRef,
        firstName: attempt.outcome.connection.firstName,
        alreadyConnected: true,
      };
    // A profile code, read by this event's scanner. It is **not** `captured`: the capture sheet
    // writes a note and a follow-up intent onto a connection *at this event*, and this connection
    // has no event to hang either on. The member gets the server's sentence, which says both that
    // they are connected and that it is not tied to this evening.
    case 'connected-outside-event':
      return { kind: 'connected-elsewhere', message: attempt.outcome.message };
    case 'refused':
      return { kind: 'refused', message: attempt.outcome.message };
  }
}

/**
 * Did this attempt log anything? The single predicate every success affordance reads.
 *
 * One function so that "did this work?" cannot be answered two ways, which is the shape of the bug
 * `MEM-031` AC-3 is written against.
 */
export function loggedAConnection(stage: ScanStage): boolean {
  return stage.kind === 'captured';
}

/**
 * `MEM-031` AC-3's sentence, on the member's side.
 *
 * It says the two things a member needs: **nothing was recorded**, and **try again**. The
 * organizer's door says the same thing in its own words (`CONNECTION_FAILED_MESSAGE`), and the
 * pairing is deliberate — one room, one wifi signal, one answer.
 */
export const SCAN_UNREACHABLE_MESSAGE =
  "That scan didn't reach us, so nothing was logged. Try again when you have a signal.";

/**
 * The other failure: **the server answered, and the answer was an error.**
 *
 * An expired session, a fault, a request this client built wrong. Nothing was logged either way,
 * so the member is told the same substantive thing — but not the same *sentence*, because
 * "try again when you have a signal" is untrue advice for somebody whose signal is fine, and a
 * member who follows it walks toward a window for no reason.
 *
 * `EVT-031` AC-5 draws the identical line on the organizer's side and says why: *"a refused scan
 * is a conversation with a working server"*, and treating one as a connectivity failure sends a
 * volunteer to paper because three people showed the wrong code.
 */
export const SCAN_FAILED_MESSAGE =
  'Something went wrong and nothing was logged. Try that scan again.';

/**
 * Which failure was it?
 *
 * Duck-typed on `ApiRequestError`'s `statusCode` rather than by importing it: this module is pure
 * and is loaded directly by `tsx --test`, and reaching into `@h2hspark/ui` would pull a React
 * component tree in behind one property check. A thrown value carrying a numeric `statusCode` came
 * *from* a response; a `TypeError` from `fetch` did not.
 *
 * **Neither branch is a success.** That is the invariant `MEM-031` AC-3 protects, and it is why
 * this returns a stage rather than a boolean — there is no way to spell "assume it worked".
 */
export function scanFailureStage(error: unknown): ScanStage {
  const answered =
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { statusCode?: unknown }).statusCode === 'number';
  return answered ? { kind: 'refused', message: SCAN_FAILED_MESSAGE } : { kind: 'unreachable' };
}

/**
 * `MEM-031` AC-4, written down next to the code that would otherwise grow the control.
 *
 * **There is no undo on a logged connection, and this screen offers none.** Not a swipe-to-remove
 * on a met-card, not a "not this person" on the capture sheet, not an X on a roster row. A control
 * that removed the row would be `MEM-034`'s Disconnect arriving without the sentence
 * `MEM-033`'s screen attaches to it — *"Removes them from your connections and clears your notes.
 * They won't be notified"* — and a member tapping it here would not have read that.
 */
/**
 * Not rendered today — `capture-dialog.tsx` points at it from a comment — but written as copy,
 * because a `NotYetAvailable` one import from a screen is a sentence somebody will read.
 *
 * `stage` said `MEM-034`, which `NotYetAvailablePanel` renders as *"Arrives with MEM-034"*: a
 * ticket number, and a stale one — `MEM-034` is `in-review` at 7/7 and Disconnect is on the
 * connection detail screen right now. This is not a thing that is coming, it is a thing that
 * lives somewhere else, and it now says so.
 *
 * `MEM-031` AC-4 is the criterion refusing the undo, and `MEM-033` owns the consequence sentence
 * quoted above. Both stay in the comment.
 */
export const NO_UNDO_IS_NOT_BUILT = {
  label: 'Remove this connection',
  reason:
    'There is no undo here. Removing a connection is on the connection’s own screen, where the ' +
    'consequence — the other person is not told, and your notes are cleared — is stated before ' +
    'the tap.',
  stage: 'your connections, where it already lives',
} as const;

// ─── MEM-031 AC-1, AC-2: the scan surfaces on responsive web ────────────────────────────────────

/**
 * Can this device actually run the scanner?
 *
 * `MEM-031` AC-1: *"The scan surfaces require a camera and a phone in the hand; on responsive web
 * they are explained rather than approximated."* This is that sentence as a predicate, and it is a
 * pure function of three facts the caller reads off the browser so that it can be tested without
 * one.
 *
 * **A laptop with a webcam is still not a phone.** `hasCamera` alone would open the scanner on a
 * desktop, where the requirement is to explain — and the member would find themselves holding a
 * laptop up to somebody's phone. So the gate is camera **and** a touch-primary device.
 *
 * `isSecureContext` is here because `getUserMedia` does not exist outside one, so a page served
 * over plain HTTP would render a scanner whose camera can never start.
 */
export function canRunScanner(device: {
  readonly hasCamera: boolean;
  readonly isTouchPrimary: boolean;
  readonly isSecureContext: boolean;
}): boolean {
  return device.hasCamera && device.isTouchPrimary && device.isSecureContext;
}

/**
 * What the web experience says instead (`MEM-031` AC-2).
 *
 * *"States what the app does and how to get it, rather than rendering a disabled scanner."* Both
 * halves matter, and the second is the one that is easy to get wrong: **a disabled scanner on a
 * laptop is worse than no scanner**, because it implies the feature should work here and does not.
 * So this screen draws no frame, no greyed-out capture button and no "scanning unavailable" chip —
 * it says what the app is for and where to open it.
 *
 * The connect **code** is not covered by this and is still drawn: a member on a laptop can have
 * their code scanned by somebody else's phone, which is half the feature working exactly as
 * intended. The requirement is about the *scan surfaces*, not about the whole area.
 */
export const SCANNER_NEEDS_A_PHONE_TITLE = 'Scanning needs the app on your phone';
export const SCANNER_NEEDS_A_PHONE_DESCRIPTION =
  'Scanning somebody’s code uses your phone’s camera, so it only works in the H2HSpark app. Open h2hspark.com/app on your phone and add it to your home screen — your code below still works here, and anybody can scan it.';
export const SCANNER_NEEDS_A_PHONE_ACTION = 'How to install the app';

/**
 * Where the install instructions live.
 *
 * `MEM-043` owns install and the supported-platform list; this points at it rather than
 * reproducing it, so there is one description of how to get the app and it is not this file's.
 */
export const INSTALL_HELP_PATH = '/app';
